import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ClipboardCheck, FileText, Calculator, Briefcase, ShieldCheck, ArrowRight, Building2, GraduationCap, Sparkles, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isFreeZonesOpen, setIsFreeZonesOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);
  const location = useLocation();

  // Small close delay prevents the mega menu from closing on brief pointer
  // gaps (crossing the trigger-to-menu whitespace, quick jitter, or a
  // navigation-induced layout shift). Same logic applies to every dropdown.
  const closeTimers = useRef({ services: null, freezones: null, company: null, insights: null });

  const setters = {
    services: setIsServicesOpen,
    freezones: setIsFreeZonesOpen,
    company: setIsCompanyOpen,
    insights: setIsInsightsOpen
  };

  const openMenu = (key) => {
    if (closeTimers.current[key]) {
      clearTimeout(closeTimers.current[key]);
      closeTimers.current[key] = null;
    }
    setters[key]?.(true);
  };

  const scheduleClose = (key) => {
    if (closeTimers.current[key]) clearTimeout(closeTimers.current[key]);
    closeTimers.current[key] = setTimeout(() => {
      setters[key]?.(false);
      closeTimers.current[key] = null;
    }, 180);
  };

  // Close all menus on route change so the dropdown never lingers after
  // a click-through navigation.
  useEffect(() => {
    setIsServicesOpen(false);
    setIsFreeZonesOpen(false);
    setIsCompanyOpen(false);
    setIsInsightsOpen(false);
    Object.keys(closeTimers.current).forEach((k) => {
      if (closeTimers.current[k]) {
        clearTimeout(closeTimers.current[k]);
        closeTimers.current[k] = null;
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Company', path: '/about', dropdown: 'company' },
    { name: 'Services', path: '/services', dropdown: 'services' },
    { name: 'Free Zones', path: '/free-zone-services-uae', dropdown: 'freezones' },
    { name: 'Industries', path: '/industries' },
    { name: 'Insights', path: '/blog', dropdown: 'insights' },
    { name: 'Contact', path: '/contact' }
  ];

  // Company mega menu — grouped links with icons + descriptions +
  // a 'Featured Guide' promo card as the third column. Middle
  // 'News & Engagement' section is intentionally omitted until content is ready.
  const companyGroups = [
    {
      key: 'our-firm',
      title: 'Our Firm',
      icon: Building2,
      iconAccent: 'bg-teal-100 text-teal-700',
      items: [
        { name: 'About WPAA', desc: 'Firm background and how we work', path: '/about' },
        { name: 'Why Choose Us', desc: 'Our approach and client commitments', path: '/why-choose-us' },
        { name: 'Team & Expertise', desc: 'Leadership and delivery teams', path: '/team' },
        { name: 'Careers', desc: 'Join the WPAA team', path: '/careers' },
        { name: 'Contact', desc: 'Talk to our team', path: '/contact' }
      ]
    },
    {
      key: 'resources',
      title: 'Resources',
      icon: GraduationCap,
      iconAccent: 'bg-emerald-100 text-emerald-700',
      items: [
        { name: 'WPAA Academy', desc: 'Practical UAE tax & compliance guides', path: '/knowledge-academy' },
        { name: 'UAE Corporate Tax Guide', desc: 'Registration, rates and filing', path: '/knowledge-academy/uae-corporate-tax-guide-2026' },
        { name: 'UAE VAT Guide', desc: 'Registration, returns and compliance', path: '/knowledge-academy/uae-vat-guide-2026' },
        { name: 'UAE Customs Duty Guide', desc: 'HS codes, imports and procedures', path: '/knowledge-academy/uae-customs-duty-guide' },
        { name: 'AML & goAML Compliance Guide', desc: 'DNFBP registration and CDD', path: '/knowledge-academy/uae-goaml-aml-compliance-guide' }
      ]
    }
  ];

  const companyFeaturedGuide = {
    eyebrow: 'Featured Guide',
    title: 'UAE Corporate Tax',
    desc: 'A practical guide covering 0% and 9% rates, registration, taxable income, Small Business Relief and Free Zone rules.',
    cta: 'Read the guide',
    path: '/knowledge-academy/uae-corporate-tax-guide-2026'
  };

  // Free Zones mega menu — 3 emirate groups + featured card.
  // Matches the visual language of Services and Company mega menus.
  const freeZoneGroups = [
    {
      key: 'sharjah',
      title: 'Sharjah Free Zones',
      icon: MapPin,
      iconAccent: 'bg-teal-100 text-teal-700',
      items: [
        { name: 'Hamriyah Free Zone', desc: "Sharjah's industrial and maritime free zone", path: '/hamriyah-free-zone-services' },
        { name: 'SAIF Zone', desc: 'Sharjah Airport International Free Zone', path: '/saif-zone-services' },
        { name: 'Shams', desc: 'Sharjah Media City for creative businesses', path: '/shams-services' },
        { name: 'SPC Free Zone', desc: 'Sharjah Publishing City for media firms', path: '/spc-free-zone-services' }
      ]
    },
    {
      key: 'dubai',
      title: 'Dubai Free Zones',
      icon: MapPin,
      iconAccent: 'bg-emerald-100 text-emerald-700',
      items: [
        { name: 'DMCC', desc: "Dubai's commodities & multi-sector zone", path: '/dmcc-audit-services' },
        { name: 'DAFZA', desc: 'Dubai Airport Free Zone for cargo & trade', path: '/dafza-services' },
        { name: 'Dubai Internet City', desc: "Dubai's ICT & technology cluster", path: '/dubai-internet-city-services' },
        { name: 'IFZA', desc: 'International Free Zone for flexible setup', path: '/ifza-services' }
      ]
    },
    {
      key: 'northern',
      title: 'Northern Emirates',
      icon: MapPin,
      iconAccent: 'bg-cyan-100 text-cyan-700',
      items: [
        { name: 'Ajman Free Zone', desc: "Ajman's SME-focused free zone", path: '/ajman-free-zone-services' },
        { name: 'RAKEZ', desc: 'Ras Al Khaimah Economic Zone', path: '/rakez-services' }
      ]
    }
  ];

  const freeZoneFeatured = {
    eyebrow: 'Getting Started',
    title: 'Setting up in a UAE Free Zone?',
    desc: 'Audit, tax, VAT, Corporate Tax and business setup support across every major UAE free zone.',
    cta: 'View all zones',
    path: '/free-zone-services-uae'
  };

  const insightsLinks = [
    { name: 'Blog', path: '/blog' },
    { name: 'WPAA Academy', path: '/knowledge-academy' }
  ];

  const serviceLinks = [
    { name: 'All Services', path: '/services' },
    { name: 'Audit Services', path: '/audit-services-uae' },
    { name: 'VAT Services', path: '/vat-services-uae' },
    { name: 'E-Invoicing', path: '/e-invoicing-uae' },
    { name: 'Corporate Tax', path: '/corporate-tax-uae' },
    { name: 'Accounting Services', path: '/accounting-services-uae' },
    { name: 'Business Advisory', path: '/business-advisory-uae' },
    { name: 'SME Business Package', path: '/sme-business-services-uae' },
    { name: 'Company Formation', path: '/company-formation-uae' },
    { name: 'goAML Compliance', path: '/services/goaml-compliance' },
    { name: 'Valuation Services', path: '/services/valuation' }
  ];

  // Grouped structure for the desktop mega menu.
  // Content and URLs are identical to serviceLinks above — grouping is UI only.
  const serviceCategories = [
    {
      title: 'Audit & Assurance',
      icon: ClipboardCheck,
      items: [
        { name: 'Audit Services', path: '/audit-services-uae', desc: 'Statutory & free-zone audits' },
        { name: 'Valuation Services', path: '/services/valuation', desc: 'Business & asset valuations' }
      ]
    },
    {
      title: 'Tax Services',
      icon: FileText,
      items: [
        { name: 'Corporate Tax', path: '/corporate-tax-uae', desc: 'UAE 9% CT registration & filing' },
        { name: 'VAT Services', path: '/vat-services-uae', desc: 'Registration, returns & FTA support' },
        { name: 'E-Invoicing', path: '/e-invoicing-uae', desc: 'Readiness, ASP & integration support' }
      ]
    },
    {
      title: 'Accounting',
      icon: Calculator,
      items: [
        { name: 'Accounting Services', path: '/accounting-services-uae', desc: 'Bookkeeping & monthly reporting' },
        { name: 'SME Business Package', path: '/sme-business-services-uae', desc: 'All-in-one compliance for SMEs' }
      ]
    },
    {
      title: 'Advisory',
      icon: Briefcase,
      items: [
        { name: 'Business Advisory', path: '/business-advisory-uae', desc: 'Growth & operational advisory' },
        { name: 'Company Formation', path: '/company-formation-uae', desc: 'Mainland & free-zone setup' }
      ]
    },
    {
      title: 'Compliance',
      icon: ShieldCheck,
      items: [
        { name: 'goAML Compliance', path: '/services/goaml-compliance', desc: 'AML & DNFBP registration' }
      ]
    }
  ];

  const freeZoneLinks = freeZoneGroups.flatMap((g) => g.items);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-md'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-transform hover:scale-105 py-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_company-home-8/artifacts/9p4fwt1a_image.png" 
              alt="White Paper Logo" 
              className="w-52 md:w-64 lg:w-72 h-auto object-contain"
              style={{ maxHeight: '70px' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => {
              if (link.dropdown) {
                const key = link.dropdown;
                const isOpen =
                  key === 'services' ? isServicesOpen :
                  key === 'freezones' ? isFreeZonesOpen :
                  key === 'company' ? isCompanyOpen :
                  isInsightsOpen;
                const items =
                  key === 'freezones' ? freeZoneLinks :
                  key === 'insights' ? insightsLinks :
                  key === 'services' ? serviceLinks : null;
                const pathPrefix =
                  key === 'freezones' ? 'free-zone' :
                  key === 'company' ? 'company' :
                  key === 'insights' ? 'insights' :
                  'services';
                const activeOnPrefix =
                  isActive(link.path) ||
                  (key === 'freezones'
                    ? location.pathname.includes('free-zone') || location.pathname.includes('saif-zone') || location.pathname.includes('hamriyah')
                    : key === 'company'
                    ? ['/about', '/why-choose-us', '/team', '/careers', '/contact', '/knowledge-academy'].some((p) => location.pathname.startsWith(p))
                    : key === 'insights'
                    ? location.pathname.startsWith('/blog') || location.pathname.startsWith('/knowledge-academy')
                    : location.pathname.startsWith('/services'));
                return (
                  <div
                    key={link.path}
                    className="relative"
                    onMouseEnter={() => openMenu(key)}
                    onMouseLeave={() => scheduleClose(key)}
                    data-testid={`nav-dropdown-${pathPrefix}`}
                  >
                    <Link
                      to={link.path}
                      onFocus={() => openMenu(key)}
                      onBlur={() => scheduleClose(key)}
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                      className={`text-sm font-medium transition-colors hover:text-teal-600 relative group flex items-center ${
                        activeOnPrefix ? 'text-teal-600' : 'text-gray-700'
                      }`}
                    >
                      {link.name}
                      <ChevronDown size={16} className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      <span
                        className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-600 transition-all group-hover:w-full ${
                          activeOnPrefix ? 'w-full' : ''
                        }`}
                      />
                    </Link>

                    {isOpen && (
                      key === 'insights' ? (
                        // Compact dropdown (Insights only) — outer wrapper carries
                        // the pt-2 gap so the hover hitbox is continuous.
                        <div className="absolute top-full left-0 pt-2 z-50">
                          <div className="w-60 bg-white rounded-lg shadow-lg border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2">
                            {items.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`block px-4 py-2 text-sm hover:bg-teal-50 hover:text-teal-600 transition-colors ${
                                  isActive(item.path) ? 'text-teal-600 bg-teal-50' : 'text-gray-700'
                                }`}
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : key === 'freezones' ? (
                        // Free Zones Mega Menu — matches Services visual language:
                        // same wrapper, same header row, same item type (font-medium
                        // title + text-[11px] description + slide-in arrow indicator).
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
                          data-testid="freezones-mega-menu"
                        >
                          <div className="w-[820px] xl:w-[880px] 2xl:w-[960px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2">
                            <div className="h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600" />

                            {/* Header row */}
                            <div className="px-8 pt-6 pb-4 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
                              <p className="text-xs font-semibold tracking-widest text-teal-600 uppercase mb-1">UAE Free Zones</p>
                              <h3 className="text-lg font-bold text-gray-900">
                                Sharjah • Dubai • Northern Emirates
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Business setup, audit, and tax advisory across the UAE&apos;s major free zones.
                              </p>
                            </div>

                            {/* 4-column body — padding + gap match Services (px-8 py-6 / gap-6) */}
                            <div className="px-8 py-6 grid grid-cols-4 gap-6">
                              {freeZoneGroups.map((group, idx) => {
                                const GroupIcon = group.icon;
                                const isLast = idx === freeZoneGroups.length - 1;
                                return (
                                  <div
                                    key={group.key}
                                    className={!isLast ? 'pr-6 border-r border-gray-100' : ''}
                                  >
                                    <div className="flex items-center gap-2 mb-4">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${group.iconAccent}`}>
                                        <GroupIcon size={16} strokeWidth={2} />
                                      </div>
                                      <h4 className="text-[11px] font-bold tracking-wider text-gray-900 uppercase">
                                        {group.title}
                                      </h4>
                                    </div>
                                    <ul className="space-y-3">
                                      {group.items.map((item) => {
                                        const active = isActive(item.path);
                                        return (
                                          <li key={item.path}>
                                            <Link
                                              to={item.path}
                                              className="group/link block -mx-2 px-2 py-1.5 rounded-md hover:bg-teal-50/60 transition-colors"
                                              data-testid={`freezone-link-${item.path.replace(/\//g, '-')}`}
                                            >
                                              <div className={`flex items-center justify-between text-sm font-medium leading-tight ${active ? 'text-teal-700' : 'text-gray-800 group-hover/link:text-teal-700'}`}>
                                                <span>{item.name}</span>
                                                <ArrowRight
                                                  size={12}
                                                  className="opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all text-teal-600"
                                                />
                                              </div>
                                              {item.desc && (
                                                <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                                                  {item.desc}
                                                </p>
                                              )}
                                            </Link>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                );
                              })}

                              {/* Featured card (Column 4) */}
                              <Link
                                to={freeZoneFeatured.path}
                                className="block bg-gradient-to-br from-teal-600 to-emerald-700 text-white rounded-xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group/promo"
                                data-testid="freezones-mega-featured"
                              >
                                <div className="flex items-center gap-2 mb-3">
                                  <Sparkles size={16} className="text-teal-200" />
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-teal-200">
                                    {freeZoneFeatured.eyebrow}
                                  </p>
                                </div>
                                <h4 className="text-base font-bold mb-2 leading-snug">{freeZoneFeatured.title}</h4>
                                <p className="text-xs text-teal-100 leading-snug mb-4">
                                  {freeZoneFeatured.desc}
                                </p>
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white">
                                  {freeZoneFeatured.cta}
                                  <ArrowRight size={14} className="group-hover/promo:translate-x-1 transition-transform" />
                                </span>
                              </Link>
                            </div>

                            {/* Footer */}
                            <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                              <Link to="/free-zone-services-uae" className="text-sm font-semibold text-teal-700 hover:text-teal-800 flex items-center gap-1.5 group/all" data-testid="freezones-mega-all-link">
                                View all free zones
                                <ArrowRight size={14} className="group-hover/all:translate-x-1 transition-transform" />
                              </Link>
                              <Link
                                to="/contact"
                                className="text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                                data-testid="freezones-mega-consultation-cta"
                              >
                                Free Consultation
                                <ArrowRight size={14} />
                              </Link>
                            </div>
                          </div>
                        </div>
                      ) : key === 'company' ? (
                        // Company Mega Menu — matches Services visual language:
                        // widened wrapper (3-column content sits comfortably),
                        // font-medium item titles, text-[11px] descriptions,
                        // slide-in arrow indicator on hover, column separators.
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
                          data-testid="company-mega-menu"
                        >
                          <div className="w-[880px] xl:w-[960px] 2xl:w-[1040px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2">
                            {/* Top gradient accent */}
                            <div className="h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600" />

                            {/* Header row */}
                            <div className="px-8 pt-6 pb-4 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
                              <p className="text-xs font-semibold tracking-widest text-teal-600 uppercase mb-1">Our Firm</p>
                              <h3 className="text-lg font-bold text-gray-900">
                                About WPAA • Our Team • Resources
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Get to know the firm, our leadership and the practical guides we publish.
                              </p>
                            </div>

                            {/* 3-column body — padding + gap match Services (px-8 py-6 / gap-6) */}
                            <div className="px-8 py-6 grid grid-cols-3 gap-6">
                              {companyGroups.map((group, idx) => {
                                const GroupIcon = group.icon;
                                const isLast = idx === companyGroups.length - 1;
                                return (
                                  <div
                                    key={group.key}
                                    className={!isLast ? 'pr-6 border-r border-gray-100' : ''}
                                  >
                                    <div className="flex items-center gap-2 mb-4">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${group.iconAccent}`}>
                                        <GroupIcon size={16} strokeWidth={2} />
                                      </div>
                                      <h4 className="text-[11px] font-bold tracking-wider text-gray-900 uppercase">
                                        {group.title}
                                      </h4>
                                    </div>
                                    <ul className="space-y-2.5">
                                      {group.items.map((item) => {
                                        const active = isActive(item.path);
                                        return (
                                          <li key={item.path}>
                                            <Link
                                              to={item.path}
                                              className="group/link block -mx-2 px-2 py-1 rounded-md hover:bg-teal-50/60 transition-colors"
                                              data-testid={`company-link-${item.path.replace(/\//g, '-')}`}
                                            >
                                              <div className={`flex items-center justify-between text-sm font-medium leading-tight ${active ? 'text-teal-700' : 'text-gray-800 group-hover/link:text-teal-700'}`}>
                                                <span>{item.name}</span>
                                                <ArrowRight
                                                  size={12}
                                                  className="opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all text-teal-600"
                                                />
                                              </div>
                                              {item.desc && (
                                                <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                                                  {item.desc}
                                                </p>
                                              )}
                                            </Link>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                );
                              })}

                              {/* Featured guide card */}
                              <Link
                                to={companyFeaturedGuide.path}
                                className="block bg-gradient-to-br from-teal-600 to-emerald-700 text-white rounded-xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group/promo"
                                data-testid="company-mega-featured-guide"
                              >
                                <div className="flex items-center gap-2 mb-3">
                                  <Sparkles size={16} className="text-teal-200" />
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-teal-200">
                                    {companyFeaturedGuide.eyebrow}
                                  </p>
                                </div>
                                <h4 className="text-lg font-bold mb-2">{companyFeaturedGuide.title}</h4>
                                <p className="text-sm text-teal-100 leading-snug mb-4">
                                  {companyFeaturedGuide.desc}
                                </p>
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white">
                                  {companyFeaturedGuide.cta}
                                  <ArrowRight size={14} className="group-hover/promo:translate-x-1 transition-transform" />
                                </span>
                              </Link>
                            </div>

                            {/* Footer */}
                            <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                              <Link to="/about" className="text-sm font-semibold text-teal-700 hover:text-teal-800 flex items-center gap-1.5 group/all" data-testid="company-mega-about-link">
                                About the firm
                                <ArrowRight size={14} className="group-hover/all:translate-x-1 transition-transform" />
                              </Link>
                              <Link
                                to="/contact"
                                className="text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                                data-testid="company-mega-consultation-cta"
                              >
                                Free Consultation
                                <ArrowRight size={14} />
                              </Link>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Premium Services Mega Menu — outer wrapper carries the
                        // pt-3 gap so the hover hitbox is continuous from trigger
                        // to menu (prevents flicker when the cursor crosses the
                        // whitespace between the trigger and the menu).
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
                          data-testid="services-mega-menu"
                        >
                          <div className="w-[820px] xl:w-[880px] 2xl:w-[960px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2">
                          {/* Top gradient accent */}
                          <div className="h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600" />

                          {/* Header row */}
                          <div className="px-8 pt-6 pb-4 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
                            <p className="text-xs font-semibold tracking-widest text-teal-600 uppercase mb-1">Our Services</p>
                            <h3 className="text-lg font-bold text-gray-900">
                              Audit • Tax • Accounting • Advisory • Compliance
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              A full compliance stack for UAE mainland and free-zone businesses.
                            </p>
                          </div>

                          {/* 5-column grid */}
                          <div className="px-8 py-6 grid grid-cols-5 gap-6">
                            {serviceCategories.map((cat, idx) => {
                              const CatIcon = cat.icon;
                              const isLast = idx === serviceCategories.length - 1;
                              return (
                                <div
                                  key={cat.title}
                                  className={!isLast ? 'pr-6 border-r border-gray-100' : ''}
                                >
                                  <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                                      <CatIcon size={16} className="text-teal-600" />
                                    </div>
                                    <h4 className="text-[11px] font-bold tracking-wider text-gray-900 uppercase">
                                      {cat.title}
                                    </h4>
                                  </div>
                                  <ul className="space-y-3">
                                    {cat.items.map((item) => {
                                      const active = isActive(item.path);
                                      return (
                                        <li key={item.path}>
                                          <Link
                                            to={item.path}
                                            className="group/link block -mx-2 px-2 py-1.5 rounded-md hover:bg-teal-50/60 transition-colors"
                                            data-testid={`mega-link-${item.path.replace(/\//g, '-')}`}
                                          >
                                            <div className={`flex items-center justify-between text-sm font-medium leading-tight ${active ? 'text-teal-700' : 'text-gray-800 group-hover/link:text-teal-700'}`}>
                                              <span>{item.name}</span>
                                              <ArrowRight
                                                size={12}
                                                className="opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all text-teal-600"
                                              />
                                            </div>
                                            {item.desc && (
                                              <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                                                {item.desc}
                                              </p>
                                            )}
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>

                          {/* Footer CTA row */}
                          <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                            <Link
                              to="/services"
                              className="text-sm font-semibold text-teal-700 hover:text-teal-800 flex items-center gap-1.5 group/all"
                              data-testid="mega-view-all-services"
                            >
                              View all services
                              <ArrowRight size={14} className="group-hover/all:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                              to="/contact"
                              className="text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                              data-testid="mega-consultation-cta"
                            >
                              Free Consultation
                              <ArrowRight size={14} />
                            </Link>
                          </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-teal-600 relative group ${
                    isActive(link.path) ? 'text-teal-600' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-600 transition-all group-hover:w-full ${
                      isActive(link.path) ? 'w-full' : ''
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white px-6 transition-all hover:shadow-lg">
                Free Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-teal-600 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t bg-white shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto animate-in slide-in-from-top">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => {
                if (link.dropdown) {
                  const key = link.dropdown;
                  // Build a flat item list for the mobile expansion.
                  // Services + Free Zones include an "All ..." entry whose path
                  // equals the parent nav path, so filter those out to avoid
                  // duplicating the parent link. Company + Insights sub-items
                  // never share the parent's path, so no filter needed there.
                  const rawItems =
                    key === 'freezones' ? freeZoneGroups.flatMap((g) => g.items) :
                    key === 'insights' ? insightsLinks :
                    key === 'services' ? serviceLinks :
                    key === 'company'
                      ? companyGroups.flatMap((g) => g.items)
                      : [];
                  const items =
                    key === 'company' || key === 'insights' || key === 'freezones'
                      ? rawItems
                      : rawItems.filter((s) => s.path !== link.path);
                  return (
                    <div key={link.path}>
                      <Link
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`text-sm font-medium transition-colors hover:text-teal-600 px-2 py-2 block ${
                          isActive(link.path) ? 'text-teal-600 bg-teal-50 rounded' : 'text-gray-700'
                        }`}
                      >
                        {link.name}
                      </Link>
                      <div className="pl-4 space-y-1">
                        {items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`text-sm transition-colors hover:text-teal-600 px-2 py-2 block ${
                              isActive(item.path) ? 'text-teal-600 bg-teal-50 rounded' : 'text-gray-500'
                            }`}
                          >
                            → {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-colors hover:text-teal-600 px-2 py-2 ${
                      isActive(link.path) ? 'text-teal-600 bg-teal-50 rounded' : 'text-gray-700'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white mt-2">
                  Free Consultation
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
